import styled from "@emotion/styled";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { API } from "../../../API";
import useOpen from "../../../hooks/useOpen";
import {
  CaregiverComplianceItemsMobileResponse,
  CaregiverUploadDocumentMobile,
  LocalDate,
} from "../../../messages";
import { STRINGS } from "../../../strings";
import { translate } from "../../../translate";
import {
  getDocumentTypeByURL,
  mapItemWithDocumentWithNewExpiryDate,
  mapItemWithoutDocument,
} from "../../../utils/complianceUtils";
import { AppBottomSheet } from "../../Generic/AppBottomSheet";
import { AppButton } from "../../Generic/AppButton";
import { AppListTitle } from "../../Generic/AppList";
import AppModal from "../../Generic/AppModal/AppModal";
import { AppOverlay } from "../../Generic/AppOverlay";
import useModal from "../../useModal";
import ComplianceDocumentScreen from "./ComplianceDocumentScreen";
import SetExpiryDateComplianceDocumentModal from "./SetExpiryDateComplianceDocumentModal";

interface Props {
  api: API;
  document: CaregiverUploadDocumentMobile;
  onClickGoBack: () => void;
}

function ComplianceDocumentActivity(props: Props) {
  const modal = useModal();
  const popupMenu = useOpen();
  const editDocumentExpiryDateModal = useOpen();
  const removeDocumentModal = useOpen();
  const queryClient = useQueryClient();
  const mutateRemoveDocument = useMutation(
    () => props.api.removeMedflytPassportDocument(props.document.id),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(["compliance_items"]);

        const previous = queryClient.getQueryData<CaregiverComplianceItemsMobileResponse>([
          "compliance_items",
        ]);

        if (previous !== undefined) {
          queryClient.setQueryData(["compliance_items"], {
            ...previous,
            items: previous.items.map((item) => mapItemWithoutDocument(item, props.document.id)),
          });
        }

        return { previous };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(["compliance_items"], context?.previous);

        modal.setModalAsError({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["compliance_items"]);
        removeDocumentModal.actions.dismiss();
        props.onClickGoBack();
      },
    }
  );
  const mutateDocumentExpiryDate = useMutation(
    (expiryDate: LocalDate | null) =>
      props.api.updateCaregiverDocumentUpload(props.document.id, { expiryDate }),
    {
      onMutate: async (expiryDate) => {
        await queryClient.cancelQueries(["compliance_items"]);

        const previous = queryClient.getQueryData<CaregiverComplianceItemsMobileResponse>([
          "compliance_items",
        ]);

        if (previous !== undefined) {
          queryClient.setQueryData(["compliance_items"], {
            ...previous,
            items: previous.items.map((item) =>
              mapItemWithDocumentWithNewExpiryDate(item, props.document.id, expiryDate)
            ),
          });
        }

        return { previous };
      },
      onError: () => {
        modal.setModalAsError({
          title: translate(STRINGS.ERROR),
          message: translate(STRINGS.SOMETHING_WENT_WRONG),
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["compliance_items"]);
        editDocumentExpiryDateModal.actions.dismiss();
      },
    }
  );

  const handleClickSetExpirationDate = () => {
    popupMenu.actions.dismiss();
    editDocumentExpiryDateModal.actions.open();
  };

  const handleClickRemoveDocument = () => {
    popupMenu.actions.dismiss();
    removeDocumentModal.actions.open();
  };

  return (
    <>
      <ComplianceDocumentScreen
        id={props.document.id}
        url={props.document.url}
        type={getDocumentTypeByURL(props.document.url)}
        name={props.document.documentNamesTrail[props.document.documentNamesTrail.length - 1]}
        expires={props.document.expires}
        onClickPopupMenuButton={popupMenu.actions.open}
        onClickGoBack={props.onClickGoBack}
      />
      <AppOverlay>
        <ComplianceDocumentActivity.MenuModal
          isOpen={popupMenu.isOpen}
          expires={props.document.expires}
          onClickSetExpirationDate={handleClickSetExpirationDate}
          onClickRemove={handleClickRemoveDocument}
          onRequestClose={popupMenu.actions.dismiss}
        />
        <ComplianceDocumentActivity.ConfirmRemoveModal
          isOpen={removeDocumentModal.isOpen}
          isLoading={mutateRemoveDocument.isLoading}
          onClickRemove={mutateRemoveDocument.mutate}
          onRequestClose={removeDocumentModal.actions.dismiss}
        />
        <SetExpiryDateComplianceDocumentModal
          isOpen={editDocumentExpiryDateModal.isOpen}
          isLoading={mutateDocumentExpiryDate.isLoading}
          expiryDate={props.document.expiryDate}
          onClickSave={mutateDocumentExpiryDate.mutate}
          onClickClose={editDocumentExpiryDateModal.actions.dismiss}
        />
        <AppModal {...modal.modalProps} />
      </AppOverlay>
    </>
  );
}

ComplianceDocumentActivity.MenuModal = (props: {
  isOpen: boolean;
  expires: boolean;
  onClickSetExpirationDate: () => void;
  onClickRemove: () => void;
  onRequestClose: () => void;
}) => {
  return (
    <AppBottomSheet isOpen={props.isOpen} rounded={true} onRequestClose={props.onRequestClose}>
      <ComplianceDocumentActivity.ModalButtonsWrapper>
        {props.expires ? (
          <AppButton variant="soft" onClick={props.onClickSetExpirationDate}>
            {translate(STRINGS.SET_EXPIRATION_DATE)}
          </AppButton>
        ) : null}
        <AppButton variant="outline" color="danger" onClick={props.onClickRemove}>
          {translate(STRINGS.REMOVE)}
        </AppButton>
      </ComplianceDocumentActivity.ModalButtonsWrapper>
    </AppBottomSheet>
  );
};

ComplianceDocumentActivity.ConfirmRemoveModal = (props: {
  isOpen: boolean;
  isLoading: boolean;
  onClickRemove: () => void;
  onRequestClose: () => void;
}) => {
  return (
    <AppBottomSheet isOpen={props.isOpen} rounded={true} onRequestClose={props.onRequestClose}>
      <AppListTitle>
        {translate(STRINGS.ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THIS_DOCUMENT)}
      </AppListTitle>
      <ComplianceDocumentActivity.ModalButtonsWrapper>
        <AppButton
          variant="filled"
          color="danger"
          onClick={props.onClickRemove}
          isLoading={props.isLoading}
        >
          {translate(STRINGS.REMOVE)}
        </AppButton>
        <AppButton variant="outline" color="black" onClick={props.onRequestClose}>
          {translate(STRINGS.CANCEL)}
        </AppButton>
      </ComplianceDocumentActivity.ModalButtonsWrapper>
    </AppBottomSheet>
  );
};

ComplianceDocumentActivity.ModalButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  button {
    width: 100%;
  }
`;

export default ComplianceDocumentActivity;
